

















import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from "@vue/composition-api";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import usePlayer from "@/hooks/usePlayer";
import { isIOS, isMobile } from "@/lib/browserUtils";
import { getStereoFormat, isStereoFile } from "@/lib/fileHelpers";
import config from "@/config";

interface HTMLDL8VideoElement extends HTMLVideoElement {
  start: () => void;
}

export default defineComponent({
  name: "StereoVideo",
  props: {
    file: { type: Object as () => ContentFile, required: true },
    src: { type: String, required: true },
    videoAutoPlay: { type: Boolean, default: false },
    allowSystemFullscreen: { type: Boolean, default: false },
    videoStartTime: { type: Number, default: 0 },
  },
  setup(props, { emit }) {
    const player = ref<HTMLVideoElement | HTMLDL8VideoElement>();
    const { play, pause: pauseVideo, isMediaPlaying: videoPlaying, setVideoStartTime } = usePlayer(player);

    const useStereoPlayer = computed(() => {
      return isStereoFile(props.file) && !!config.delightVideoPlayerLicense;
    });
    const stereoFormat = computed(() => {
      return getStereoFormat(props.file);
    });

    const blockInterface = () => {
      emit("lockFullScreen", true);
    };

    const unblockInterface = () => {
      setTimeout(() => emit("lockFullScreen", false));
    };
    const playVideo = () => {
      play();
      if (useStereoPlayer.value) {
        (player.value as HTMLDL8VideoElement).start();
      }
    };

    const handleClick = () => {
      if (isIOS() && isMobile()) return; //on IOS used touch events

      if (videoPlaying.value) {
        pauseVideo();
      } else {
        playVideo();
      }
    };

    watch(videoPlaying, () => {
      emit("changeVideoPlayingStatus", videoPlaying.value);
    });

    onMounted(() => {
      if (!player.value) return;
      if (useStereoPlayer.value) {
        playVideo();
        player.value.addEventListener("play", blockInterface);
        document.addEventListener("x-dl8-evt-did-exit", unblockInterface);
      } else {
        if (props.videoStartTime === 0 || props.videoAutoPlay) {
          playVideo();
        }
        setVideoStartTime(props.videoStartTime);
        player.value.ontimeupdate = () => {
          emit("currentTimeChange", player.value?.currentTime);
        };
      }
    });

    onBeforeUnmount(() => {
      if (useStereoPlayer.value && player.value) {
        player.value.removeEventListener("play", blockInterface);
        document.removeEventListener("x-dl8-evt-did-exit", unblockInterface);
      }
    });

    return {
      useStereoPlayer,
      player,
      stereoFormat,
      handleClick,
    };
  },
});
